import { FieldValue, Timestamp } from "@angular/fire/firestore";
import { AssetModel } from "./asset.model";
import { FileModel } from "./file.model";
import { MM_ControlInventoryModel } from "./mmControlManagement.model";

export interface MM_MaterialDetails {
  type: string;
  state: string;
  subType: string;
  materialName: string;
  supplierName?: string;
  isImpregnated: boolean;
}
export interface MM_ConsumtionVolume {
  value: number;
  unit: string;
}
export type MM_Properties = {
  odor?: string;
  lead?: string;
  dusty?: string;
  nickel?: string;
  mercury?: string;
  arsenic?: string;
  cadmium?: string;
  moisture?: string;
  pumpable?: string;
  burnTest?: string;
  antimony?: string;
  chromium?: string;
  fluoride?: string;
  viscosity?: string;
  particles?: string;
  flashPoint?: string;
  stickiness?: string;
  pH?: number | string;
  flowability?: string;
  wholeTires?: boolean;
  storedInEnclosedAsset?: boolean;
  waterContent?: string;
  doesntIgnite?: boolean;
  foreignBodies?: string;
  sedimentation?: string;
  totalNitrogen?: string;
  fireHPhrase?: string[];
  leachateTest?: boolean;
  calorificValue?: string;
  totalPhosphorus?: string;
  healthHPhrase?: string[];
  flammabilityRange?: string;
  timeToIgnitionTest?: string;
  lowerFlammableLevel?: string;
  upperFlammableLevel?: string;
  totalSuspendedSolids?: string;
  environmentHPhrase?: string[];
  particleSizeDistribution?: string;
  naturalUntreatedBiomass?: boolean;
  environmentCategoryLiquid?: string;
  bodBiochemicalOxygenDemand?: string;
  totalPetroleumHydrocarbons?: string;
  sustainedFlamingDurationTest?: string;
};

export interface OCR_FileModel extends FileModel {
  isNewFile?: boolean;
}

export interface MM_RiskLevelModel {
  fireRiskLevel: number;
  healthRiskLevel: number;
  environmentRiskLevel: number;
}

export interface MM_Identification {
  isNewSds?: string;
  hazards?: string[];
  riskLevel: MM_RiskLevelModel;
  criticalWastes?: string[];
  dartxClassification?: string;
  properties?: MM_Properties;
  sdsFileInfo?: OCR_FileModel;
  labSheets?: OCR_FileModel[];
  statusSection?: { status: string };
  materialDetails?: MM_MaterialDetails;
  consumtionVolume?: MM_ConsumtionVolume;
  confirmationOfBannedWaste?: BannedWasteItemModel[];
  environmentalPermitsStatus?: MM_EnvironmentalPermitsStatus;
}

export interface MM_RiskLevelRequestUpdateInfo_Model {
  id?: string;
  remarks?: string;
  rejectionReason?: string;
  materialId?: string;
  fireRiskLevel: number;
  requestStatus?: string;
  fireHazards?: string[];
  healthRiskLevel: number;
  healthHazards?: string[];
  requestedByEmail?: string;
  environmentRiskLevel: number;
  requestedByUserName?: string;
  environmentHazards?: string[];
  createdAt?: string;
  requestAction?: {
    email?: string;
    remarks?: string;
    fullName?: string;
    createdAt?: string;
  };
}

export interface HazardDisplayItemModel {
  title: string;
  iconPath?: string;
}

export interface MM_FireHazardModel {
  area?: string;
  level?: string;
  description?: string;
  riskLevel?: number;
}
export interface GroupedHazardSection {
  label: string;
  icon: string;
  items: HazardDisplayItemModel[];
}
export interface MM_Health_EnvModel {
  level: string;
  riskLevel: number;
  oelRange?: string;
  description: string;
  heavyMetals?: string;
}

export interface MM_EnvironmentHazard {
  level: string;
  riskLevel: number;
  description: string;
  persistantPollutants?: string;
}

export interface MM_Hazards {
  fire: MM_FireHazardModel;
  health: MM_Health_EnvModel;
  environment: MM_EnvironmentHazard;
}

export interface MM_Newly_Asset {
  uid: string;
  zone?: string;
}

export interface OCRExtractedDataModel {
  id?: string;
  type?: string;
  state?: string;
  fileId?: string;
  subType?: string;
  fileInfo?: FileModel;
  fileCategory?: string;
  materialName?: string;
  mm_inventory_id?: string;
  properties?: MM_Properties;
}

export interface MM_EnvironmentalPermitsStatus {
  storageAndTransport: string;
  preAndCoProcessing: string;
  trailBurnPersistentOrganicPollutants: string;
}

export interface MM_ActivityModel {
  id?: string;
  title?: string;
  status?: string;
  createdAt?: string;
  description?: string;
  createdByEmail?: string;
  createdByUserName?: string;
  assignedTo?: { name: string; email: string; createdAt?: string | Date }[];
}

export interface MM_InventoryModel {
  id?: string;
  siteId: string;
  regionId: string;
  countryId: string;
  hazard: MM_Hazards;
  isPublished?: boolean;
  updatedByEmail?: string;
  isConfirmEditing?: boolean;
  updatedByUserName?: string;
  identification?: MM_Identification;
  editLockedAt?: Timestamp | FieldValue | null;
  assetLink: {
    assets: MM_LinkedAssetModel[];
    simulatedAssetes: MM_Newly_Asset[];
    addCompletedAssetZone: string[];
  }; // Adjust if you have a specific type
  completedZone: string[];
  finalReport: {
    assignTo: { name: string; email: string; userName?: string };
    ccEmails: (string | { name: string; email: string; userName?: string })[];
    subject: string;
    mailDescription: string;
  };
  safetyDataSheet?: MM_FileModel;
  isCertified?: boolean;
  isExpertFlow?: boolean; // Optional since not present in data
  stepperInfo?: {
    isCompleted: boolean;
    currentStepNo: number;
  };
  status?: string;
  isView?: boolean;
  isClone?: boolean;
  cloneDataInfo?: {
    parentId: string;
    isCertified?: boolean;
  };
  ocrFilledFields?: string[];
  introductionPrompt?: string;
  controlSheetPdfInfo?: FileModel;
  shouldGenerateControlSheet?: boolean;
  ocrExtractedData?: {
    sdsFileData: OCRExtractedDataModel;
    labSheetsData: OCRExtractedDataModel[];
  };
  masterMaterialId?: string;
  activities?: MM_ActivityModel[];
  controlSheetData?: MM_ControlSheetToPdfDataModel;
  createdDate?: string;
}
export interface MM_FileModel {
  id: string;
  src: string;
}
export interface MM_InventoryListResponseModel {
  status: boolean;
  message: string;
  data: MM_InventoryModel;
}

export interface MM_StateInfo {
  state: "Solid" | "Liquid" | "Gas" | "Sludge";
  units: string[];
}

export interface MM_Types {
  type: string;
  subType: string[];
}

export interface MM_MaterialTypes {
  id: string;
  createdByEmail: string;
  createdDate: string;
  stateInfo: MM_StateInfo[];
  types: MM_Types[];
}

export interface MMInventoryMasterModel {
  id: string;
  type: string;
  usage: string;
  state?: string;
  family: string;
  subType: string;
  commentsUse: string;
  hazardClass: string;
  reachCasReference: string;
}

export interface MM_MaterialTypesResponseModel {
  status: boolean;
  message: string;
  data: MMInventoryMasterModel[];
}

export interface MM_HazardItem {
  id: string;
  createdByEmail: any;
  createdDate: string;
  stateInfo: MM_StateInfo[];
  fire: MM_Health_EnvModel[];
  health: MM_Health_EnvModel[];
  environment: MM_Health_EnvModel[];
}

export interface MM_HazardsResponseModel {
  data: MM_HazardItem[];
  status: boolean;
  message: string;
}

export interface MM_LinkedAssetModel {
  uid: string;
  id: string;
  assetZone: { area?: string; coordinates?: { lat: number; lng: number } };
  isLinked: boolean;
  icon_url?: string;
}
export interface GroupedHazardSection {
  label: string;
  icon: string;
  items: HazardDisplayItemModel[]; // Changed from MM_HazardItem to HazardDisplayItemModel
}
export enum MM_InventoryStatusEnum {
  DRAFT = "Draft",
  APPROVED = "Approved",
  REJECTED = "Rejected",
  PUBLISHED = "Published",
  PENDING_APPROVAL = "Pending Approval",
}
export enum MM_InventoryStepEnum {
  LINK_TO_ASSET = "linkToAsset",
  CONFIRMATION = "confirmation",
  GLOBAL_SEARCH = "globalSearch",
  IDENTIFICATION = "identification",
}

export interface Update_MM_InventoryBodyDataModel {
  id: string;
  status: string;
  rejectionReason?: string;
}

export interface Update_MM_InventoryResponseModel {
  data?: Object;
  status: boolean;
  message: string;
}

export interface MM_GroupedControl {
  controlType: string;
  items: MM_ControlInventoryModel[];
}

export interface MM_GroupedHazard {
  hazardName: string;
  controls: MM_GroupedControl[];
}

export interface MM_GroupedMaterialAndHazard {
  assetName?: string;
  assetType?: string;
  assetIcon?: string;
  assetIconUrl?: string;
  assetTypeTitle?: string;
  hazardsArray: MM_GroupedHazard[];
}

export interface MM_GroupedAssetOnly {
  assetTypeTitle: string;
  items: MM_ControlInventoryModel[];
}

export interface MM_ControlSheetToPdfDataModel {
  id: string;
  type: string;
  state: string;
  status: string;
  subType: string;
  siteName?: string;
  materialName: string;
  envRiskLevel: number;
  introduction: string;
  fireRiskLevel: number;
  healthRiskLevel: number;
  linkedAssets: AssetModel[];
  selectedHazards?: string[];
  introductionPrompt?: string;
  consumptionVolumeUnit: string;
  consumptionVolumeValue: string;
  tableData: MM_ProcessedControlItem[];
  fireHazards?: HazardDisplayItemModel[];
  healthHazards?: HazardDisplayItemModel[];
  environmentHazards?: HazardDisplayItemModel[];
}

export interface MM_ControlSheetToPdfResponseModel {
  status: boolean;
  message: string;
}

export interface MM_ControlSheetToPdfDataModel {
  id: string;
  type: string;
  state: string;
  status: string;
  subType: string;
  siteName?: string;
  materialName: string;
  envRiskLevel: number;
  introduction: string;
  fireRiskLevel: number;
  healthRiskLevel: number;
  linkedAssets: AssetModel[];
  selectedHazards?: string[];
  consumptionVolumeUnit: string;
  consumptionVolumeValue: string;
  tableData: MM_ProcessedControlItem[];
  fireHazards?: HazardDisplayItemModel[];
  healthHazards?: HazardDisplayItemModel[];
  environmentHazards?: HazardDisplayItemModel[];
}

export interface MM_ProcessedControlItem {
  assetName: string;
  assetIcon?: string;
  assetIconUrl?: string;
  assetImages?: string[];
  assetTypeTitle?: string;
  assetImagesBase64?: string[];
  hazards: {
    hazardName: string;

    controls: {
      controlType: string;
      level1: string | null;
      level2: string | null;
      level3: string | null;
      bestInClass: string | null;
    }[];
  }[];
}
export interface MM_Control {
  controlType: string;
  bestInClass: string | null;
  level1: string | null;
  level2: string | null;
  level3: string | null;
  minLevelIndex?: number;
  controlImages?: string[];
}

export interface MM_ControlSheetIntroResponseModel {
  data: string;
  status: boolean;
  message: string;
}

export interface BannedWasteItemModel {
  title: string;
  popupText: string;
  isShowPopup: boolean;
  isDisableSubmit: boolean;
  isPresent: boolean | null;
}
export type MM_OCRPropertiesLevelMapping = {
  id: string;
  low: string[];
  high: string[];
  medium: string[];
};

export interface AddOCRNotMappedPropertyReqBodyModel {
  value: string;
  siteId?: string;
  siteName?: string;
  propertyId: string;
  fileInfo?: FileModel;
  propertyName: string;
  fileCategory?: string;
  materialName?: string;
  mm_inventory_id?: string;
}

export type OcrMappedLevel = "high" | "medium" | "low" | "not applicable";
export interface ocrNotMappedDataModel {
  id?: string;
  propertyId?: string;
  propertyName?: string;
  value?: string;
  mappedValue?: string;
  isMapped?: boolean;
  fileCategory?: string;
  fileInfo?: Partial<OCR_FileModel>;
  siteName?: string;
  status?: boolean;
}
export interface OcrRowData {
  id: string;
  propertyId: string;
  propertyName: string;
  originalValue: string;
  mappedValue: OcrMappedLevel;
  fileCategory?: string;
  isMapped: boolean;
  isEdited: boolean;
  selected: boolean;
  fileInfo: OCR_FileModel;
}
export interface MM_Hazard_HealthModel {
  id: string;
  state: string;
  riskLevel: string;
  HPhrases: string[];
}
// OCR-specific interfaces for partial patching
export interface OCRPatchMaterialDetails {
  type?: string;
  state?: string;
  subType?: string;
  materialName?: string;
  supplierName?: string;
  isImpregnated?: string;
}

export interface OCROverwritePayload extends OCRExtractedDataModel {
  [key: string]: any;
}
export interface OCRPatchProperties {
  pH?: string;
  odor?: string;
  dusty?: string;
  moisture?: string;
  pumpable?: string;
  particles?: string;
  viscosity?: string;
  flashPoint?: string;
  stickiness?: string;
  flowability?: string;
  foreignBodies?: string;
  sedimentation?: string;
  fireHPhrase?: string[];
  calorificValue?: string;
  healthHPhrase?: string[];
  environmentHPhrase?: string[];
  particleSizeDistribution?: string;
}

export interface OCRValidatedPatch {
  identificationFormGroup: {
    materialDetails?: OCRPatchMaterialDetails;
    properties?: OCRPatchProperties;
  };
}

export interface HazardsInfoDoc {
  id?: string;
  fireHazards: HazardDisplayItemModel[];
  healthHazards: HazardDisplayItemModel[];
  environmentHazards: HazardDisplayItemModel[];
}
export interface PdfInputHelpFile {
  fileSrc: string;
  fileName: string;
}
export interface UpdateRiskLevelResponseModel {
  materialId?: string;
  createdByEmail?: string;
  environmentRiskLevel?: number;
  fireRiskLevel?: number;
  healthRiskLevel?: number;

  requestStatus: string;
  rejectionReason: string | null;
  updatedDate: string;
  updatedByEmail: string | null;
  updatedByUserName: string;

  requestAction: {
    createdAt: string;
    email: string | null;
    fullName: string;
    remarks: string;
  };
}

export interface MM_Hazard_EnvironmentModel {
  id: string;           
  code: string; 
  state: string;      
  title?: string;
  riskLevel: number;   
  category: string;
  HPhrases: string[];  
  description: string;
  createdAt?: string;
}

export enum MM_Inventory_ActivityTypeEnum {
  DRAFT_SAVED = "DRAFT_SAVED",
  MATERIAL_REJECTED = "MATERIAL_REJECTED",
  MATERIAL_APPROVED = "MATERIAL_APPROVED",
  REGISTRATION_STARTED = "REGISTRATION_STARTED",
  SUBMITTED_FOR_APPROVAL = "SUBMITTED_FOR_APPROVAL",
  RISK_LEVEL_UPDATE_REQUESTED = "RISK_LEVEL_UPDATE_REQUESTED",
}

export type StepType =
  | "linkToAsset"
  | "globalSearch"
  | "confirmation"
  | "identification";
